import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import classes from './UploadRentRoll.module.css';
import { useFullScreenLoader } from '~/hooks/useFullScreenLoader';
import { withFullScreenLoader } from '~/hocs/withFullScreenLoader';
import { useParams } from 'react-router-dom';
import UploadRentRollCsv from '../RentRoll/UploadRentRollCsv';
import ColumnMatch from '../RentRoll/ColumnMatch';
import RentRollConfirmModal, { PreviewSummary } from '../RentRoll/RentRollConfirmModal';
import { handleRentRollConfirmError } from '../RentRoll/handleRentRollConfirmError';
import { uniqueId } from 'lodash';
import { useTenantCsvMapping } from '~/hooks/useTenantCsvMapping';
import { TenantInfo } from '../RentRoll/TenantRentRollTable';
import { confirmRentRollImport, previewRentRoll } from '~/api/resident';
import { toast } from 'react-toastify';
import { Colors } from '~/enums/Colors';

export type BuildingPreviewResult = {
  buildingName: string;
  previewId: string;
  summary: PreviewSummary;
};

const LargeCsvUpload = () => {
  const [parsedCsv, setparsedCsv] = useState<{
    rows: any[];
    maxColumns: number;
  }>();
  const [selectedStartingRow, setselectedStartingRow] = useState<any>();
  const { buildingId } = useParams<{ buildingId: string }>();
  const [currentStep, setCurrentStep] = useState<
    | 'UPLOAD_RENT_ROLL'
    | 'RENTROLL_COLUMN_SELECTION_TABLE'
    | 'RENTROLL_PREVIEW_CONFIRMATION'
    | ''
  >('UPLOAD_RENT_ROLL');
  const [selectedFile, setSelectedFile] = useState<File>();
  const [previewResults, setPreviewResults] = useState<BuildingPreviewResult[]>();
  const reset = () => setCurrentStep('');

  const { loader } = useFullScreenLoader();
  const { fieldMapping, columnNameSelected, getTargetedRows, prepareTenantInfo } =
    useTenantCsvMapping();

  const onUploadSuccess = ({
    rows,
    maxColumns,
    file
  }: {
    rows: any[];
    maxColumns: number;
    file: File;
  }) => {
    const rowsWithId = rows?.map((row) => {
      row.id = uniqueId();
      return row;
    });

    setparsedCsv({
      rows: rowsWithId,
      maxColumns
    });
    setSelectedFile(file);

    setCurrentStep('RENTROLL_COLUMN_SELECTION_TABLE');
  };

  const onStartingRowSelected = (row: any) => {
    setselectedStartingRow(row);
    const element = document.getElementById(`column-match-radio-${row.id}`);

    if (element?.attributes) {
      // @ts-ignore
      element.checked = true;
    }
  };

  function groupTenantsByBuilding(tenants: TenantInfo[]) {
    return tenants.reduce((acc: { [key: string]: TenantInfo[] }, tenant) => {
      const buildingName = tenant.buildingName;
      if (!acc[buildingName]) {
        acc[buildingName] = [];
      }
      acc[buildingName].push({
        ...tenant,
        email: tenant.email ? tenant.email.toLowerCase() : '',
      });
      return acc;
    }, {});
  }

  const displayTenantsImportSuccessAlert = (totalExistingLeads: number, totalLeadsCreated: number) => {
    alert(
      `Tenants successfully imported.
      \n Existing Leads - ${totalExistingLeads}
      \n Leads created - ${totalLeadsCreated}`
    );
  };

  const previewMutation = useMutation(
    async (currentSelectedFile: File) => {
      const targetRows = getTargetedRows(
        parsedCsv?.rows ?? [],
        selectedStartingRow.id
      );

      const tenants = prepareTenantInfo({
        rows: targetRows,
        buildingId: buildingId,
        requireBuildingNameColumn: true
      });

      const groupedTenants = groupTenantsByBuilding(tenants);

      return Promise.all(
        Object.values(groupedTenants).map(async (currentBuildingTenants) => {
          const { buildingName } = currentBuildingTenants[0];
          const res = await previewRentRoll({
            file: currentSelectedFile,
            tenants: currentBuildingTenants,
            buildingName
          });
          return { buildingName, ...res.data } as BuildingPreviewResult;
        })
      );
    },
    {
      onSuccess: (results) => {
        setPreviewResults(results);
        setCurrentStep('RENTROLL_PREVIEW_CONFIRMATION');
      },
      onError: (err: any) => {
        toast.error(err?.message || 'Failed to generate preview. Please try again.');
      }
    }
  );

  const confirmMutation = useMutation(
    async (previews: BuildingPreviewResult[]) => {
      const settled = await Promise.allSettled(
        previews.map((preview) => confirmRentRollImport(preview.previewId))
      );
      return previews.map((preview, i) => ({ preview, outcome: settled[i] }));
    },
    {
      onSuccess: (outcomes) => {
        const succeeded = outcomes.filter(
          (o): o is typeof o & { outcome: PromiseFulfilledResult<any> } =>
            o.outcome.status === 'fulfilled'
        );
        const failed = outcomes.filter(
          (o): o is typeof o & { outcome: PromiseRejectedResult } =>
            o.outcome.status === 'rejected'
        );

        if (failed.length === 0) {
          const totalExistingLeads = succeeded.reduce(
            (acc, o) => acc + (o.outcome.value.data?.result?.intercomExistingLeads?.length ?? 0),
            0
          );
          const totalLeadsCreated = succeeded.reduce(
            (acc, o) => acc + (o.outcome.value.data?.result?.intercomLeadsCreated?.length ?? 0),
            0
          );

          toast.success('Rent roll confirmed for all buildings.');
          displayTenantsImportSuccessAlert(totalExistingLeads, totalLeadsCreated);
          setCurrentStep('');
          setPreviewResults(undefined);
          setparsedCsv(undefined);
          setSelectedFile(undefined);
          return;
        }

        if (succeeded.length > 0) {
          const succeededBuildingNames = succeeded
            .map((o) => o.preview.buildingName)
            .join(', ');
          toast.success(`Confirmed for: ${succeededBuildingNames}.`);
        }

        // Each failure gets its own status-specific toast (409/410/404 vs generic) —
        // the same messaging the single-building flow shows via handleRentRollConfirmError.
        // 404s (already processed/gone) can't be retried and are dropped; 409/410s
        // (stale roster/expired) can be retried once the preview is regenerated.
        const retryable = failed.filter((o) => {
          const action = handleRentRollConfirmError(o.outcome.reason, {
            pluralBuildings: true
          });
          return action.returnToColumnMapping;
        });

        setPreviewResults(retryable.length > 0 ? retryable.map((o) => o.preview) : undefined);
        setCurrentStep(retryable.length > 0 ? 'RENTROLL_COLUMN_SELECTION_TABLE' : '');
      }
    }
  );

  useEffect(() => {
    if (previewMutation.isLoading || confirmMutation.isLoading) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [previewMutation.isLoading, confirmMutation.isLoading]);

  const triggerPreview = () => {
    if (!selectedStartingRow?.id) {
      return toast.error('Starting row not selected');
    }

    if (!selectedFile) {
      return toast.error('No file found — please re-upload.');
    }

    try {
      previewMutation.mutate(selectedFile);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className={classes.pageWrapper}>
      {['', 'UPLOAD_RENT_ROLL', 'UPLOAD_MOVEOUT_LIST'].includes(
        currentStep
      ) && (
        <div
          className={`flex-row align-start justify-between ${classes.headerWrapper}`}
        >
          <div>
            <div
              className='margin-bottom-16'
              style={{ fontSize: '32px', color: Colors.cobuDarkBlue }}
            >
              Large CSV Upload
            </div>
            <p>
            From this page, you will upload a CSV file which contains many buildings active rent roll. Then you will map the columns and then process the file for multiple buildings.
            </p>
          </div>
        </div>
      )}
      <UploadRentRollCsv
        renderMode='inline'
        onUpload={onUploadSuccess}
      />
      {currentStep == 'RENTROLL_COLUMN_SELECTION_TABLE' && parsedCsv && (
        <ColumnMatch
          rows={parsedCsv.rows}
          fieldMapping={fieldMapping}
          maxColumns={parsedCsv.maxColumns}
          onRowSelection={onStartingRowSelected}
          selectedRow={selectedStartingRow}
          buildingId={buildingId}
          onUpload={triggerPreview}
          onColumnNameSelected={columnNameSelected}
          onCancel={reset}
          uploadActionTitle={'Upload Residents'}
        />
      )}
      {previewResults && (
        <RentRollConfirmModal
          isVisible={currentStep === 'RENTROLL_PREVIEW_CONFIRMATION'}
          previews={previewResults.map(({ buildingName, summary }) => ({
            buildingName,
            summary
          }))}
          isConfirming={confirmMutation.isLoading}
          onConfirm={() => confirmMutation.mutate(previewResults)}
          onCancel={() => setCurrentStep('RENTROLL_COLUMN_SELECTION_TABLE')}
        />
      )}
    </div>
  );
};

export default withFullScreenLoader(LargeCsvUpload);
